import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  Users, Plus, Search, Filter, Edit2, Trash2, 
  Phone, Mail, MapPin, ShieldCheck, Wallet, Eye,
  AlertCircle, Info, Hash, User, AlertTriangle, CheckCircle,
  XCircle, UserCog, Clock, Link2, Unlink, CheckSquare,
  Briefcase, ArrowRight, RefreshCw, X, ArrowUpDown, ArrowUp, ArrowDown,
  IndianRupee, Crown, Star, Award, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { ClickToCall } from '../components/ClickToCall';

import { API_URL } from '../utils/apiUrl';

const formatCurrency = (value) => {
  if (!value) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  return `₹${value?.toLocaleString('en-IN') || 0}`;
};

// Sortable column header component - must be outside main component to avoid React warning
const SortableHeader = ({ field, label, sortConfig, onSort, className = "" }) => {
  const isActive = sortConfig.key === field;
  return (
    <TableHead 
      className={`text-slate-400 cursor-pointer hover:text-white transition-colors select-none ${className}`}
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive ? (
          sortConfig.direction === 'desc' ? 
            <ArrowDown className="w-3 h-3 text-[#C5A059]" /> : 
            <ArrowUp className="w-3 h-3 text-[#C5A059]" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-40" />
        )}
      </div>
    </TableHead>
  );
};

// Tier configuration - same as Principal (use lowercase codes)
const TIER_CONFIG = {
  platinum: { icon: Crown, color: 'text-cyan-400', bgColor: 'bg-cyan-500/20', borderColor: 'border-cyan-500/30', label: 'Platinum' },
  gold: { icon: Star, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', borderColor: 'border-yellow-500/30', label: 'Gold' },
  silver: { icon: Award, color: 'text-gray-300', bgColor: 'bg-gray-500/20', borderColor: 'border-gray-500/30', label: 'Silver' },
  blue: { icon: User, color: 'text-blue-400', bgColor: 'bg-blue-500/20', borderColor: 'border-blue-500/30', label: 'Blue' }
};

// Dormant status badge configuration
const getDormantBadge = (dormantStatus) => {
  if (!dormantStatus) return null;
  const statusLower = dormantStatus.toLowerCase();
  
  const config = {
    'active': { label: 'Active', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    'dormant_1': { label: 'Dormant 1', className: 'bg-lime-500/20 text-lime-400 border-lime-500/30' },
    'dormant_2': { label: 'Dormant 2', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    'dormant_3': { label: 'Dormant 3', className: 'bg-red-500/20 text-red-400 border-red-500/30' }
  };
  
  const statusConfig = config[statusLower] || config['active'];
  return (
    <Badge className={`text-xs ${statusConfig.className}`}>
      {statusConfig.label}
    </Badge>
  );
};

const ClientsPage = () => {
  const { user, token, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('directory');
  
  // Client Directory State
  const [clients, setClients] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [rms, setRms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isPortfolioDialogOpen, setIsPortfolioDialogOpen] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSegment, setFilterSegment] = useState('all');
  const [filterKyc, setFilterKyc] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [clientCodeError, setClientCodeError] = useState('');
  const [checkingClientCode, setCheckingClientCode] = useState(false);
  const [flaggedClients, setFlaggedClients] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'brokerage_ytd', direction: 'desc' }); // Default: brokerage_ytd desc
  
  // Tier tabs state (for RM/Dealer)
  const [activeTierTab, setActiveTierTab] = useState('all');
  const [tierClients, setTierClients] = useState([]);
  const [tierLoading, setTierLoading] = useState(false);
  const [tierSortConfig, setTierSortConfig] = useState({ key: 'brokerage_ytd', direction: 'desc' });

  // Client Mapping State
  const [mappingStats, setMappingStats] = useState(null);
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [mappingSearchQuery, setMappingSearchQuery] = useState('');
  const [filterDealer, setFilterDealer] = useState('all');
  const [filterRM, setFilterRM] = useState('all');
  const [mappingFilterSegment, setMappingFilterSegment] = useState('all');
  const [mappingFilterStatus, setMappingFilterStatus] = useState('all');
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState('');
  const [bulkSelectedValue, setBulkSelectedValue] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editDealer, setEditDealer] = useState('');
  const [editRM, setEditRM] = useState('');

  const [formData, setFormData] = useState({
    client_code: '',
    name: '',
    email: '',
    phone: '',
    pan: '',
    segment: 'retail',
    kyc_status: 'pending',
    status: 'active',
    address: '',
    city: '',
    state: '',
    aum: 0,
    assigned_dealer_id: 'unassigned',
    assigned_rm_id: 'unassigned'
  });

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  useEffect(() => {
    fetchClients();
    if (hasRole(['principal', 'business_head'])) {
      fetchDealers();
      fetchRMs();
      fetchFlaggedClients();
      fetchMappingStats();
    }
  }, [filterSegment, filterKyc, filterStatus, sortConfig]);

  // Fetch tier clients when tier tab changes (for RM/Dealer/Principal)
  useEffect(() => {
    if (activeTierTab !== 'all') {
      fetchTierClients(activeTierTab);
    }
  }, [activeTierTab, tierSortConfig]);

  const fetchClients = async () => {
    try {
      let url = `${API_URL}/clients`;
      const params = new URLSearchParams();
      if (filterSegment !== 'all') params.append('segment', filterSegment);
      if (filterKyc !== 'all') params.append('kyc_status', filterKyc);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      // Add sorting params - backend handles sorting
      if (sortConfig.key) {
        params.append('sort_by', sortConfig.key);
        params.append('sort_order', sortConfig.direction);
      }
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients by tier (for all roles with tier tabs)
  const fetchTierClients = async (tierCode) => {
    if (!tierCode || tierCode === 'all') {
      setTierClients([]);
      return;
    }
    setTierLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('sort_by', tierSortConfig.key || 'brokerage_ytd');
      params.append('sort_order', tierSortConfig.direction || 'desc');
      params.append('page_size', '10000'); // Get all clients
      
      // Tier code is stored in lowercase in DB
      const response = await axios.get(
        `${API_URL}/tiers/clients/${tierCode.toLowerCase()}?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setTierClients(response.data.clients || []);
    } catch (error) {
      console.error('Failed to fetch tier clients:', error);
      toast.error('Failed to fetch tier clients');
      setTierClients([]);
    } finally {
      setTierLoading(false);
    }
  };

  // Fetch client portfolio/holdings for AUM drill-down
  const fetchClientPortfolio = async (clientCode) => {
    setPortfolioLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/clients/${clientCode}/portfolio`,
        { headers: getAuthHeaders() }
      );
      setPortfolioData(response.data);
      setIsPortfolioDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
      toast.error('Failed to fetch holdings');
    } finally {
      setPortfolioLoading(false);
    }
  };

  // Handle tier sort
  const handleTierSort = (key) => {
    setTierSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const fetchDealers = async () => {
    try {
      const response = await axios.get(`${API_URL}/dealers?status=Active`, { headers: getAuthHeaders() });
      setDealers(response.data);
    } catch (error) {
      console.error('Failed to fetch dealers:', error);
    }
  };

  const fetchRMs = async () => {
    try {
      const response = await axios.get(`${API_URL}/rms?status=Active`, { headers: getAuthHeaders() });
      setRms(response.data);
    } catch (error) {
      console.error('Failed to fetch RMs:', error);
    }
  };

  const fetchFlaggedClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients/flagged/missing-code`, { headers: getAuthHeaders() });
      setFlaggedClients(response.data);
    } catch (error) {
      console.error('Failed to fetch flagged clients:', error);
    }
  };

  const fetchMappingStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients/mapping-stats`, { headers: getAuthHeaders() });
      setMappingStats(response.data);
    } catch (error) {
      console.error('Failed to fetch mapping stats:', error);
    }
  };

  const validateClientCode = async (code) => {
    if (!code || code.length < 3) {
      setClientCodeError('Client Code must be at least 3 characters');
      return false;
    }
    
    if (editMode && selectedClient?.client_code === code.toUpperCase()) {
      setClientCodeError('');
      return true;
    }
    
    setCheckingClientCode(true);
    try {
      const response = await axios.get(`${API_URL}/clients/check-code/${encodeURIComponent(code)}`, {
        headers: getAuthHeaders()
      });
      if (response.data.exists) {
        setClientCodeError('This Client Code already exists');
        return false;
      }
      setClientCodeError('');
      return true;
    } catch (error) {
      const exists = clients.some(c => c.client_code?.toUpperCase() === code.toUpperCase() && c.id !== selectedClient?.id);
      if (exists) {
        setClientCodeError('This Client Code already exists');
        return false;
      }
      setClientCodeError('');
      return true;
    } finally {
      setCheckingClientCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_code) {
      setClientCodeError('Client Code is required');
      toast.error('Client Code is mandatory');
      return;
    }
    
    if (formData.phone) {
      const phoneClean = formData.phone.replace(/\D/g, '');
      if (phoneClean.length !== 10) {
        toast.error('Invalid mobile number. Enter a 10-digit mobile number.');
        return;
      }
    }
    
    const isValid = await validateClientCode(formData.client_code);
    if (!isValid && !editMode) {
      toast.error('Please fix Client Code errors');
      return;
    }
    
    try {
      const submitData = {
        client_code: formData.client_code,
        name: formData.name,
        segment: formData.segment,
        kyc_status: formData.kyc_status,
        status: formData.status,
        aum: formData.aum || 0,
      };
      
      // Only include optional fields if they have values
      if (formData.email && formData.email.trim()) submitData.email = formData.email.trim();
      if (formData.phone && formData.phone.trim()) submitData.phone = formData.phone.replace(/\D/g, '');
      if (formData.pan && formData.pan.trim()) submitData.pan = formData.pan.trim();
      if (formData.address && formData.address.trim()) submitData.address = formData.address.trim();
      if (formData.city && formData.city.trim()) submitData.city = formData.city.trim();
      if (formData.state && formData.state.trim()) submitData.state = formData.state.trim();
      if (formData.assigned_dealer_id && formData.assigned_dealer_id !== 'unassigned') {
        submitData.assigned_dealer_id = formData.assigned_dealer_id;
      }
      if (formData.assigned_rm_id && formData.assigned_rm_id !== 'unassigned') {
        submitData.assigned_rm_id = formData.assigned_rm_id;
      }
      
      if (editMode && selectedClient) {
        delete submitData.client_code;
        await axios.put(`${API_URL}/clients/${selectedClient.id}`, submitData, { headers: getAuthHeaders() });
        toast.success('Client updated successfully');
      } else {
        await axios.post(`${API_URL}/clients`, submitData, { headers: getAuthHeaders() });
        if (hasRole(['rm', 'dealer'])) {
          toast.success('Client created. Pending approval from Principal.');
        } else {
          toast.success('Client created successfully');
        }
      }
      setIsDialogOpen(false);
      resetForm();
      fetchClients();
      if (hasRole(['principal', 'business_head'])) {
        fetchFlaggedClients();
        fetchMappingStats();
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Operation failed';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Are you sure you want to delete client "${client.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/clients/${client.id}`, { headers: getAuthHeaders() });
      toast.success('Client deleted');
      fetchClients();
      if (hasRole(['principal', 'business_head'])) {
        fetchFlaggedClients();
        fetchMappingStats();
      }
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setEditMode(true);
    setFormData({
      client_code: client.client_code || '',
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      pan: client.pan || '',
      segment: client.segment || 'retail',
      kyc_status: client.kyc_status || 'pending',
      status: client.status || 'active',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      aum: client.aum || 0,
      assigned_dealer_id: client.assigned_dealer_id || 'unassigned',
      assigned_rm_id: client.assigned_rm_id || 'unassigned'
    });
    setClientCodeError('');
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      client_code: '', name: '', email: '', phone: '', pan: '',
      segment: 'retail', kyc_status: 'pending', status: 'active',
      address: '', city: '', state: '', aum: 0,
      assigned_dealer_id: 'unassigned', assigned_rm_id: 'unassigned'
    });
    setSelectedClient(null);
    setEditMode(false);
    setClientCodeError('');
  };

  // Client Mapping Functions
  const filteredMappingClients = clients.filter(client => {
    if (mappingSearchQuery) {
      const query = mappingSearchQuery.toLowerCase();
      const matchesSearch = 
        client.client_code?.toLowerCase().includes(query) ||
        client.name?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.phone?.includes(query);
      if (!matchesSearch) return false;
    }
    
    if (filterDealer !== 'all') {
      if (filterDealer === 'unmapped') {
        if (client.assigned_dealer_id) return false;
      } else if (client.assigned_dealer_id !== filterDealer) {
        return false;
      }
    }
    
    if (filterRM !== 'all') {
      if (filterRM === 'unmapped') {
        if (client.assigned_rm_id) return false;
      } else if (client.assigned_rm_id !== filterRM) {
        return false;
      }
    }
    
    if (mappingFilterSegment !== 'all' && client.segment !== mappingFilterSegment) return false;
    if (mappingFilterStatus !== 'all' && client.status !== mappingFilterStatus) return false;
    
    return true;
  });

  const toggleSelectAll = () => {
    if (selectedClients.size === filteredMappingClients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(filteredMappingClients.map(c => c.id)));
    }
  };

  const toggleSelectClient = (clientId) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
  };

  const openBulkDialog = (type) => {
    setBulkActionType(type);
    setBulkSelectedValue('');
    setBulkDialogOpen(true);
  };

  const handleBulkAssign = async () => {
    if (selectedClients.size === 0) {
      toast.error('No clients selected');
      return;
    }
    
    setBulkProcessing(true);
    try {
      const payload = { client_ids: Array.from(selectedClients) };
      
      if (bulkActionType === 'dealer') {
        payload.assigned_dealer_id = bulkSelectedValue === 'clear' ? '' : bulkSelectedValue;
      } else if (bulkActionType === 'rm') {
        payload.assigned_rm_id = bulkSelectedValue === 'clear' ? '' : bulkSelectedValue;
      }
      
      const response = await axios.post(
        `${API_URL}/clients/bulk-mapping`,
        payload,
        { headers: getAuthHeaders() }
      );
      
      const { success_count, failure_count } = response.data;
      
      if (failure_count > 0) {
        toast.warning(`Mapping updated for ${success_count} clients. ${failure_count} failed.`);
      } else {
        toast.success(`Successfully updated ${success_count} clients`);
      }
      
      setBulkDialogOpen(false);
      setSelectedClients(new Set());
      fetchClients();
      fetchMappingStats();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Bulk mapping failed';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      toast.error(errorMessage);
    } finally {
      setBulkProcessing(false);
    }
  };

  const openMappingDialog = (client) => {
    setEditingClient(client);
    setEditDealer(client.assigned_dealer_id || '');
    setEditRM(client.assigned_rm_id || '');
    setMappingDialogOpen(true);
  };

  const handleIndividualMapping = async () => {
    if (!editingClient) return;
    
    try {
      await axios.put(
        `${API_URL}/clients/${editingClient.id}`,
        {
          assigned_dealer_id: editDealer || null,
          assigned_rm_id: editRM || null
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Mapping updated for ${editingClient.client_code}`);
      setMappingDialogOpen(false);
      setEditingClient(null);
      fetchClients();
      fetchMappingStats();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Update failed';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
      }
      toast.error(errorMessage);
    }
  };

  const getDealerName = (dealerCode) => {
    if (!dealerCode) return '-';
    const dealer = dealers.find(d => d.dealer_code === dealerCode);
    return dealer ? dealer.name : dealerCode;
  };

  const getRMName = (rmCode) => {
    if (!rmCode) return '-';
    const rm = rms.find(r => r.rm_code === rmCode);
    return rm ? rm.name : rmCode;
  };

  const getStatusBadge = (status) => {
    const isActive = status === 'active';
    return (
      <Badge className={`${isActive ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'} text-xs capitalize`}>
        {isActive ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
        {status || 'Active'}
      </Badge>
    );
  };

  const getApprovalBadge = (approvalStatus) => {
    if (!approvalStatus || approvalStatus === 'approved') return null;
    if (approvalStatus === 'pending_approval') {
      return (
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs ml-2">
          <Clock className="w-3 h-3 mr-1" />
          Pending Approval
        </Badge>
      );
    }
    if (approvalStatus === 'rejected') {
      return (
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs ml-2">
          <XCircle className="w-3 h-3 mr-1" />
          Rejected
        </Badge>
      );
    }
    return null;
  };

  // Sorting handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const filteredClients = clients.filter(client => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return client.name?.toLowerCase().includes(query) ||
        client.client_code?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.phone?.includes(query);
    }
    return true;
  });

  // Since backend handles sorting, we just use filtered list
  const sortedClients = filteredClients;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Users className="w-8 h-8 text-[#C5A059]" />
            Client Management
          </h1>
          <p className="text-slate-400 mt-1">
            Manage client records and dealer/RM assignments
          </p>
        </div>
        
        {hasRole(['principal', 'business_head', 'dealer', 'rm']) && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-gold" data-testid="add-client-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F172A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-white">
                  {editMode ? 'Edit Client' : 'Add New Client'}
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  {editMode ? 'Update client information below.' : 'Fill in the client details. Client Code is mandatory and cannot be changed later.'}
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {/* Client Code */}
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Hash className="w-4 h-4 text-[#C5A059]" />
                    Client Code *
                    {editMode && (
                      <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">
                        Cannot be changed
                      </Badge>
                    )}
                  </Label>
                  <Input
                    value={formData.client_code}
                    onChange={(e) => {
                      setFormData({ ...formData, client_code: e.target.value.toUpperCase() });
                      if (e.target.value.length >= 3) {
                        validateClientCode(e.target.value);
                      }
                    }}
                    className={`input-vault ${clientCodeError ? 'border-red-500' : ''}`}
                    placeholder="e.g., CLI001"
                    disabled={editMode}
                    data-testid="client-code-input"
                  />
                  {clientCodeError && (
                    <p className="text-sm text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {clientCodeError}
                    </p>
                  )}
                  {checkingClientCode && (
                    <p className="text-sm text-slate-400">Checking availability...</p>
                  )}
                </div>

                {/* Name & PAN */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Full Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-vault"
                      placeholder="Client name"
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">PAN</Label>
                    <Input
                      value={formData.pan}
                      onChange={(e) => setFormData({ ...formData, pan: e.target.value.toUpperCase() })}
                      className="input-vault"
                      placeholder="ABCDE1234F"
                      maxLength={10}
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Phone *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setFormData({ ...formData, phone: value });
                      }}
                      className="input-vault"
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      data-testid="client-phone-input"
                    />
                    {formData.phone && formData.phone.length !== 10 && (
                      <p className="text-xs text-amber-400">Enter 10 digit mobile number</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-vault"
                      placeholder="email@example.com"
                      data-testid="client-email-input"
                    />
                  </div>
                </div>

                {/* Segment, KYC, Status */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Segment</Label>
                    <Select value={formData.segment} onValueChange={(v) => setFormData({ ...formData, segment: v })}>
                      <SelectTrigger className="input-vault">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="hni">HNI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">KYC Status</Label>
                    <Select value={formData.kyc_status} onValueChange={(v) => setFormData({ ...formData, kyc_status: v })}>
                      <SelectTrigger className="input-vault">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger className="input-vault">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Assignment (Principal/Business Head only) */}
                {hasRole(['principal', 'business_head']) && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Assign to Dealer</Label>
                      <Select value={formData.assigned_dealer_id} onValueChange={(v) => setFormData({ ...formData, assigned_dealer_id: v })}>
                        <SelectTrigger className="input-vault">
                          <SelectValue placeholder="Select dealer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {dealers.map(d => (
                            <SelectItem key={d.dealer_code} value={d.dealer_code}>
                              {d.name} ({d.dealer_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Assign to RM</Label>
                      <Select value={formData.assigned_rm_id} onValueChange={(v) => setFormData({ ...formData, assigned_rm_id: v })}>
                        <SelectTrigger className="input-vault">
                          <SelectValue placeholder="Select RM" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {rms.map(r => (
                            <SelectItem key={r.rm_code} value={r.rm_code}>
                              {r.name} ({r.rm_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Address */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="input-vault"
                    placeholder="Street address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="input-vault"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="input-vault"
                    />
                  </div>
                </div>

                {/* AUM */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Initial AUM (₹)</Label>
                  <Input
                    type="number"
                    value={formData.aum}
                    onChange={(e) => setFormData({ ...formData, aum: parseFloat(e.target.value) || 0 })}
                    className="input-vault"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-white/10">
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-gold" data-testid="submit-client-btn">
                    {editMode ? 'Update Client' : 'Create Client'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid ${hasRole(['principal', 'business_head']) ? 'grid-cols-2' : 'grid-cols-1'} w-full max-w-md bg-slate-800/50 border border-slate-700`}>
          <TabsTrigger 
            value="directory" 
            className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black text-slate-400"
            data-testid="tab-client-directory"
          >
            <Users className="w-4 h-4 mr-2" />
            Client Directory
          </TabsTrigger>
          {hasRole(['principal', 'business_head']) && (
            <TabsTrigger 
              value="mapping" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-400"
              data-testid="tab-client-mapping"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Client Mapping
            </TabsTrigger>
          )}
        </TabsList>

        {/* Client Directory Tab */}
        <TabsContent value="directory" className="space-y-6 mt-6">
          {/* Flagged Clients Warning */}
          {hasRole(['principal', 'business_head']) && flaggedClients.length > 0 && (
            <Card className="card-vault border-amber-500/30 bg-amber-500/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-amber-400 font-semibold">Data Integrity Warning</h4>
                    <p className="text-slate-400 text-sm mt-1">
                      {flaggedClients.length} client(s) are missing a Client Code. These clients cannot be used in transactional contexts until fixed.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search */}
          <Card className="card-vault">
            <CardContent className="p-4">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, client code, email, or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input-vault pl-10"
                    data-testid="client-search-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Clients Table - with Tier Tabs for all roles */}
          <Card className="card-vault">
            <CardHeader className="border-b border-white/10">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#C5A059]" />
                  {activeTierTab !== 'all' 
                    ? `${TIER_CONFIG[activeTierTab]?.label || activeTierTab} Clients (${tierClients.length})`
                    : `Clients (${sortedClients.length})`
                  }
                </CardTitle>
                
                {/* Tier Filter Tabs for all roles - inline in header */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={activeTierTab === 'all' ? 'default' : 'outline'}
                    onClick={() => setActiveTierTab('all')}
                    className={activeTierTab === 'all' 
                      ? 'bg-[#C5A059] text-black font-bold text-sm px-4 py-2' 
                      : 'border-white/20 text-slate-300 hover:text-white hover:border-white/40 font-semibold text-sm px-4 py-2'}
                    data-testid="tier-tab-all"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    All
                  </Button>
                  <Button
                    variant={activeTierTab === 'platinum' ? 'default' : 'outline'}
                    onClick={() => setActiveTierTab('platinum')}
                    className={activeTierTab === 'platinum' 
                      ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-bold text-sm px-4 py-2 shadow-lg shadow-cyan-500/30' 
                      : 'border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-400 font-semibold text-sm px-4 py-2'}
                    data-testid="tier-tab-platinum"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Platinum
                  </Button>
                  <Button
                    variant={activeTierTab === 'gold' ? 'default' : 'outline'}
                    onClick={() => setActiveTierTab('gold')}
                    className={activeTierTab === 'gold' 
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold text-sm px-4 py-2 shadow-lg shadow-yellow-500/30' 
                      : 'border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10 hover:border-yellow-400 font-semibold text-sm px-4 py-2'}
                    data-testid="tier-tab-gold"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Gold
                  </Button>
                  <Button
                    variant={activeTierTab === 'silver' ? 'default' : 'outline'}
                    onClick={() => setActiveTierTab('silver')}
                    className={activeTierTab === 'silver' 
                      ? 'bg-gradient-to-r from-gray-400 to-gray-500 text-black font-bold text-sm px-4 py-2 shadow-lg shadow-gray-400/30' 
                      : 'border-gray-400/40 text-gray-300 hover:bg-gray-400/10 hover:border-gray-300 font-semibold text-sm px-4 py-2'}
                    data-testid="tier-tab-silver"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Silver
                  </Button>
                  <Button
                    variant={activeTierTab === 'blue' ? 'default' : 'outline'}
                    onClick={() => setActiveTierTab('blue')}
                    className={activeTierTab === 'blue' 
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-sm px-4 py-2 shadow-lg shadow-blue-500/30' 
                      : 'border-blue-500/40 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400 font-semibold text-sm px-4 py-2'}
                    data-testid="tier-tab-blue"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Blue
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Show loading state for tier data */}
              {activeTierTab !== 'all' && tierLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        {/* Show different columns based on tier view vs all view */}
                        {activeTierTab !== 'all' ? (
                          <>
                            <SortableHeader field="client_code" label="Client Code" sortConfig={tierSortConfig} onSort={handleTierSort} />
                            <SortableHeader field="name" label="Name" sortConfig={tierSortConfig} onSort={handleTierSort} />
                            <TableHead className="text-slate-400">Contact</TableHead>
                            <SortableHeader field="segment" label="Segment" sortConfig={tierSortConfig} onSort={handleTierSort} />
                            <TableHead className="text-slate-400">KYC</TableHead>
                            <TableHead className="text-slate-400">Status</TableHead>
                            <SortableHeader field="aum" label="AUM" sortConfig={tierSortConfig} onSort={handleTierSort} className="text-right" />
                            <SortableHeader field="brokerage_ytd" label="Brokerage (YTD)" sortConfig={tierSortConfig} onSort={handleTierSort} className="text-right" />
                            <TableHead className="text-slate-400">Dormant</TableHead>
                            <TableHead className="text-slate-400 text-right">Actions</TableHead>
                          </>
                        ) : (
                          <>
                            <SortableHeader field="client_code" label="Client Code" sortConfig={sortConfig} onSort={handleSort} />
                            <SortableHeader field="name" label="Name" sortConfig={sortConfig} onSort={handleSort} />
                            <TableHead className="text-slate-400">Contact</TableHead>
                            <SortableHeader field="segment" label="Segment" sortConfig={sortConfig} onSort={handleSort} />
                            <SortableHeader field="kyc_status" label="KYC" sortConfig={sortConfig} onSort={handleSort} />
                            <SortableHeader field="status" label="Status" sortConfig={sortConfig} onSort={handleSort} />
                            <SortableHeader field="aum" label="AUM" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                            <SortableHeader field="brokerage_ytd" label="Brokerage (YTD)" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                            <TableHead className="text-slate-400">Dormant</TableHead>
                            <TableHead className="text-slate-400 text-right">Actions</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Tier View for all roles */}
                      {activeTierTab !== 'all' ? (
                        tierClients.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={10} className="text-center py-8 text-slate-400">
                              No {TIER_CONFIG[activeTierTab]?.label || activeTierTab} clients found
                            </TableCell>
                          </TableRow>
                        ) : (
                          tierClients.map((client, idx) => (
                            <TableRow key={client.id || idx} className="border-white/5 hover:bg-white/5">
                              <TableCell className="font-mono text-[#C5A059]">
                                {client.client_code}
                              </TableCell>
                              <TableCell className="text-white font-medium">
                                {client.name || client.client_name}
                              </TableCell>
                              <TableCell className="text-slate-300">
                                <div className="space-y-1 text-sm">
                                  {client.phone && (
                                    <ClickToCall
                                      phoneNumber={client.phone}
                                      clientCode={client.client_code}
                                      clientName={client.name || client.client_name}
                                      token={token}
                                    />
                                  )}
                                  {client.email && (
                                    <div className="flex items-center gap-1 text-slate-400">
                                      <Mail className="w-3 h-3" />
                                      {client.email}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={`${client.segment === 'hni' ? 'bg-[#C5A059]/20 text-[#C5A059] border-[#C5A059]/30' : 'bg-white/10 text-slate-300 border-white/10'} text-xs uppercase`}>
                                  {client.segment || 'retail'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className={`text-xs ${client.kyc_status === 'verified' ? 'bg-emerald-500/20 text-emerald-400' : client.kyc_status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                  {client.kyc_status || 'pending'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {getStatusBadge(client.status || 'active')}
                              </TableCell>
                              <TableCell className="text-right">
                                <button
                                  onClick={() => fetchClientPortfolio(client.client_code)}
                                  className="text-white font-medium hover:text-[#C5A059] cursor-pointer underline decoration-dotted underline-offset-2 transition-colors"
                                  data-testid={`tier-aum-drilldown-${client.client_code}`}
                                >
                                  {formatCurrency(client.aum)}
                                </button>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <IndianRupee className="w-3 h-3 text-emerald-400" />
                                  <span className="text-emerald-400 font-medium">
                                    {formatCurrency(client.brokerage_ytd || 0)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                {getDormantBadge(client.dormant_status)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedClient(client);
                                      setIsViewDialogOpen(true);
                                    }}
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-[#C5A059]"
                                    data-testid={`tier-view-${client.client_code}`}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {hasRole(['principal', 'business_head', 'dealer']) && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedClient(client);
                                        setEditForm({
                                          name: client.name || '',
                                          email: client.email || '',
                                          phone: client.phone || '',
                                          segment: client.segment || 'retail',
                                          kyc_status: client.kyc_status || 'pending',
                                          status: client.status || 'active',
                                          address: client.address || '',
                                          pan: client.pan || '',
                                          notes: client.notes || ''
                                        });
                                        setIsEditDialogOpen(true);
                                      }}
                                      className="h-8 w-8 p-0 text-slate-400 hover:text-blue-400"
                                      data-testid={`tier-edit-${client.client_code}`}
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )
                      ) : (
                        /* All Clients View */
                        sortedClients.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={10} className="text-center py-8 text-slate-400">
                              No clients found
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedClients.map(client => (
                            <TableRow key={client.id} className="border-white/5 hover:bg-white/5">
                              <TableCell className="font-mono text-[#C5A059]">
                                {client.client_code || (
                                  <Badge className="bg-red-500/20 text-red-400 border-red-500/30">MISSING</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center flex-wrap gap-1">
                                  <span className="text-white font-medium">{client.name}</span>
                                  {getApprovalBadge(client.approval_status)}
                                </div>
                                {client.added_by_name && client.approval_status === 'pending_approval' && (
                                  <div className="text-xs text-slate-500 mt-1">
                                    Added by: {client.added_by_name} ({client.added_by_role || 'N/A'})
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-slate-300">
                                <div className="space-y-1 text-sm">
                                  {client.phone && (
                                    <ClickToCall
                                      phoneNumber={client.phone}
                                      clientCode={client.client_code}
                                      clientName={client.name || client.client_name}
                                      token={token}
                                    />
                                  )}
                                  {client.email && (
                                    <div className="flex items-center gap-1 text-slate-400">
                                      <Mail className="w-3 h-3" />
                                      {client.email}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={`${client.segment === 'hni' ? 'bg-[#C5A059]/20 text-[#C5A059] border-[#C5A059]/30' : 'bg-white/10 text-slate-300 border-white/10'} text-xs uppercase`}>
                                  {client.segment}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge className={`text-xs ${client.kyc_status === 'verified' ? 'bg-emerald-500/20 text-emerald-400' : client.kyc_status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                  {client.kyc_status}
                                </Badge>
                              </TableCell>
                              <TableCell>{getStatusBadge(client.status)}</TableCell>
                              <TableCell className="text-right">
                                {client.client_code ? (
                                  <button
                                    onClick={() => fetchClientPortfolio(client.client_code)}
                                    className="text-white font-medium hover:text-[#C5A059] cursor-pointer underline decoration-dotted underline-offset-2 transition-colors"
                                    data-testid={`aum-drilldown-${client.client_code}`}
                                  >
                                    {formatCurrency(client.aum)}
                                  </button>
                                ) : (
                                  <span className="text-white font-medium">{formatCurrency(client.aum)}</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <IndianRupee className="w-3 h-3 text-emerald-400" />
                                  <span className="text-emerald-400 font-medium">
                                    {formatCurrency(client.brokerage_ytd || 0)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                {getDormantBadge(client.dormant_status)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {hasRole(['principal', 'business_head', 'dealer', 'rm']) && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleEdit(client)}
                                        className="hover:bg-white/10"
                                        data-testid={`edit-client-${client.id}`}
                                      >
                                        <Edit2 className="w-4 h-4 text-slate-400" />
                                      </Button>
                                      {hasRole(['principal']) && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => handleDelete(client)}
                                          className="hover:bg-red-500/20"
                                          data-testid={`delete-client-${client.id}`}
                                        >
                                          <Trash2 className="w-4 h-4 text-red-400" />
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Client Mapping Tab */}
        {hasRole(['principal', 'business_head']) && (
          <TabsContent value="mapping" className="space-y-6 mt-6">
            {/* Mapping Stats */}
            {mappingStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="card-vault">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#C5A059]/20">
                        <Users className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">{mappingStats.total_clients}</p>
                        <p className="text-sm text-slate-400">Total Clients</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="card-vault">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-amber-500/20">
                        <Unlink className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">{mappingStats.unmapped_dealer}</p>
                        <p className="text-sm text-slate-400">No Dealer</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="card-vault">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-amber-500/20">
                        <Unlink className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">{mappingStats.unmapped_rm}</p>
                        <p className="text-sm text-slate-400">No RM</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="card-vault">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <Link2 className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-white">
                          {mappingStats.total_clients - Math.max(mappingStats.unmapped_dealer, mappingStats.unmapped_rm)}
                        </p>
                        <p className="text-sm text-slate-400">Fully Mapped</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Bulk Action Bar */}
            {selectedClients.size > 0 && (
              <Card className="card-vault border-[#C5A059]/30 bg-[#C5A059]/5">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <CheckSquare className="w-5 h-5 text-[#C5A059]" />
                      <span className="text-white font-medium">
                        {selectedClients.size} client(s) selected
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => openBulkDialog('dealer')}>
                        <Briefcase className="w-4 h-4 mr-2" />
                        Assign Dealer
                      </Button>
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => openBulkDialog('rm')}>
                        <User className="w-4 h-4 mr-2" />
                        Assign RM
                      </Button>
                      <Button size="sm" variant="outline" className="border-white/10" onClick={() => setSelectedClients(new Set())}>
                        <X className="w-4 h-4 mr-2" />
                        Clear
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Mapping Filters */}
            <Card className="card-vault">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by Client Code, name, email, or phone..."
                      value={mappingSearchQuery}
                      onChange={(e) => setMappingSearchQuery(e.target.value)}
                      className="input-vault pl-10"
                    />
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Select value={filterDealer} onValueChange={setFilterDealer}>
                      <SelectTrigger className="input-vault w-40">
                        <Briefcase className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Dealer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Dealers</SelectItem>
                        <SelectItem value="unmapped">Unmapped</SelectItem>
                        {dealers.map(d => (
                          <SelectItem key={d.dealer_code} value={d.dealer_code}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select value={filterRM} onValueChange={setFilterRM}>
                      <SelectTrigger className="input-vault w-40">
                        <User className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="RM" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All RMs</SelectItem>
                        <SelectItem value="unmapped">Unmapped</SelectItem>
                        {rms.map(r => (
                          <SelectItem key={r.rm_code} value={r.rm_code}>{r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mapping Table */}
            <Card className="card-vault">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-white flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-[#C5A059]" />
                  Client Mapping ({filteredMappingClients.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-slate-400 w-12">
                          <Checkbox 
                            checked={filteredMappingClients.length > 0 && selectedClients.size === filteredMappingClients.length}
                            onCheckedChange={toggleSelectAll}
                            className="border-white/20"
                          />
                        </TableHead>
                        <TableHead className="text-slate-400">Client Code</TableHead>
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">Segment</TableHead>
                        <TableHead className="text-slate-400 text-right">AUM</TableHead>
                        <TableHead className="text-slate-400">Assigned Dealer</TableHead>
                        <TableHead className="text-slate-400">Assigned RM</TableHead>
                        <TableHead className="text-slate-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMappingClients.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                            No clients found matching filters
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredMappingClients.map(client => (
                          <TableRow 
                            key={client.id} 
                            className={`border-white/5 hover:bg-white/5 ${selectedClients.has(client.id) ? 'bg-[#C5A059]/10' : ''}`}
                          >
                            <TableCell>
                              <Checkbox 
                                checked={selectedClients.has(client.id)}
                                onCheckedChange={() => toggleSelectClient(client.id)}
                                className="border-white/20"
                              />
                            </TableCell>
                            <TableCell className="font-mono text-white">
                              {client.client_code || <span className="text-red-400">MISSING</span>}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="text-white font-medium">{client.name}</p>
                                {client.email && <p className="text-sm text-slate-400">{client.email}</p>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${client.segment === 'hni' ? 'bg-[#C5A059]/20 text-[#C5A059] border-[#C5A059]/30' : 'bg-white/10 text-slate-300 border-white/10'} text-xs uppercase`}>
                                {client.segment}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right text-white">{formatCurrency(client.aum)}</TableCell>
                            <TableCell>
                              {client.assigned_dealer_id ? (
                                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                                  {getDealerName(client.assigned_dealer_id)}
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Unassigned</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {client.assigned_rm_id ? (
                                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                                  {getRMName(client.assigned_rm_id)}
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Unassigned</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-white/10 hover:bg-white/10"
                                onClick={() => openMappingDialog(client)}
                              >
                                <UserCog className="w-4 h-4 mr-1" />
                                Map
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Bulk Mapping Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              {bulkActionType === 'dealer' ? (
                <><Briefcase className="w-5 h-5 text-blue-400" /> Assign Dealer to {selectedClients.size} Clients</>
              ) : (
                <><User className="w-5 h-5 text-purple-400" /> Assign RM to {selectedClients.size} Clients</>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action will update all selected clients and create audit logs.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">
                {bulkActionType === 'dealer' ? 'Select Dealer' : 'Select RM'}
              </Label>
              <Select value={bulkSelectedValue} onValueChange={setBulkSelectedValue}>
                <SelectTrigger className="input-vault">
                  <SelectValue placeholder={`Choose ${bulkActionType === 'dealer' ? 'dealer' : 'RM'}...`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clear">
                    <span className="text-amber-400">Clear Assignment (Unassign)</span>
                  </SelectItem>
                  {bulkActionType === 'dealer' ? (
                    dealers.map(d => (
                      <SelectItem key={d.dealer_code} value={d.dealer_code}>
                        {d.name} ({d.dealer_code})
                      </SelectItem>
                    ))
                  ) : (
                    rms.map(r => (
                      <SelectItem key={r.rm_code} value={r.rm_code}>
                        {r.name} ({r.rm_code})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)} className="border-white/10">
                Cancel
              </Button>
              <Button className="btn-gold" onClick={handleBulkAssign} disabled={!bulkSelectedValue || bulkProcessing}>
                {bulkProcessing ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><ArrowRight className="w-4 h-4 mr-2" /> Apply to {selectedClients.size} Clients</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Individual Mapping Dialog */}
      <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <UserCog className="w-5 h-5 text-[#C5A059]" />
              Edit Client Mapping
            </DialogTitle>
            {editingClient && (
              <DialogDescription className="text-slate-400">
                {editingClient.client_code} - {editingClient.name}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-blue-400" />
                Assigned Dealer
              </Label>
              <Select value={editDealer || 'unassigned'} onValueChange={(v) => setEditDealer(v === 'unassigned' ? '' : v)}>
                <SelectTrigger className="input-vault">
                  <SelectValue placeholder="Select dealer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {dealers.map(d => (
                    <SelectItem key={d.dealer_code} value={d.dealer_code}>
                      {d.name} ({d.dealer_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-2">
                <User className="w-4 h-4 text-purple-400" />
                Assigned RM
              </Label>
              <Select value={editRM || 'unassigned'} onValueChange={(v) => setEditRM(v === 'unassigned' ? '' : v)}>
                <SelectTrigger className="input-vault">
                  <SelectValue placeholder="Select RM" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {rms.map(r => (
                    <SelectItem key={r.rm_code} value={r.rm_code}>
                      {r.name} ({r.rm_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button variant="outline" onClick={() => setMappingDialogOpen(false)} className="border-white/10">
                Cancel
              </Button>
              <Button className="btn-gold" onClick={handleIndividualMapping}>
                <ArrowRight className="w-4 h-4 mr-2" />
                Save Mapping
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Portfolio/Holdings Dialog - AUM Drill-down */}
      <Dialog open={isPortfolioDialogOpen} onOpenChange={setIsPortfolioDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#C5A059]" />
              Holdings Detail
              {portfolioData && (
                <Badge className="ml-2 bg-[#C5A059]/20 text-[#C5A059]">
                  {portfolioData.client_code}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {portfolioData?.client_name} - Portfolio Holdings
            </DialogDescription>
          </DialogHeader>
          
          {portfolioLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
            </div>
          ) : portfolioData ? (
            <div className="flex-1 overflow-auto space-y-4 mt-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-slate-800/50 border-white/10">
                  <CardContent className="p-4">
                    <p className="text-slate-400 text-sm">Total AUM</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(portfolioData.total_aum)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-white/10">
                  <CardContent className="p-4">
                    <p className="text-slate-400 text-sm">Notional P&L</p>
                    <p className={`text-2xl font-bold ${portfolioData.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {portfolioData.total_pnl >= 0 ? '+' : ''}{formatCurrency(portfolioData.total_pnl)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-white/10">
                  <CardContent className="p-4">
                    <p className="text-slate-400 text-sm">Holdings Count</p>
                    <p className="text-2xl font-bold text-white">{portfolioData.holdings_count}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Holdings Table */}
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-[#0F172A]">
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-slate-400">ISIN / Security</TableHead>
                      <TableHead className="text-slate-400 text-right">Quantity</TableHead>
                      <TableHead className="text-slate-400 text-right">Close Price</TableHead>
                      <TableHead className="text-slate-400 text-right">Market Value</TableHead>
                      <TableHead className="text-slate-400 text-right">Buy Avg</TableHead>
                      <TableHead className="text-slate-400 text-right">Notional P&L</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {portfolioData.holdings?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                          No holdings found
                        </TableCell>
                      </TableRow>
                    ) : (
                      portfolioData.holdings?.map((holding, idx) => (
                        <TableRow key={holding.isin || idx} className="border-white/5 hover:bg-white/5">
                          <TableCell>
                            <div>
                              <p className="text-white font-medium">{holding.security_name || holding.scrip_name || '-'}</p>
                              <p className="text-slate-500 text-xs font-mono">{holding.isin || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-white">
                            {(holding.quantity || holding.qty || 0).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-right text-slate-300">
                            ₹{(holding.close_price || holding.ltp || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className="text-right text-white font-medium">
                            {formatCurrency(holding.holding_value || holding.market_value || 0)}
                          </TableCell>
                          <TableCell className="text-right text-slate-300">
                            ₹{(holding.buy_avg || holding.avg_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${(holding.notional_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {(holding.notional_pnl || 0) >= 0 ? '+' : ''}{formatCurrency(holding.notional_pnl || 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              No portfolio data available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
